<template>
  <section
    v-if="loaderStore.isFiltersLoading || currentList.length"
    ref="guidesBlock"
    class="guides-wrap"
  >
    <div class="wrap">
      <h2
        class="feature-title"
        data-test="guide-block-title"
      >
        {{ headString }}
      </h2>
      <component
        :is="currentComponentSkeleton"
        v-if="isSkeletonActive"
      />
      <component
        :is="currentComponent"
        v-else-if="currentList.length"
        class="guides__list"
        :current-list="currentList"
        :default-sogu-fields="defaultSoguFields"
        :has-tours="true"
      />

      <div
        v-if="showAllButton"
        class="guides__btn"
      >
        <ui-button
          :href="pathToGuides"
          :variant="UiButtonVariant.OUTLINE"
          :color-mode="UiButtonColorMode.NEUTRAL"
          text="Все гиды"
          data-test="guide-block-show-all"
          @click="allGuidesButtonClickHandler"
        />
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
  import { GuidesList } from '@/features/guidesList';
  import { GuidesSlider } from '@/features/guidesSlider';
  import TourGuidesListSkeleton from './TourGuidesListSkeleton.vue';
  import TourGuidesSlideSkeleton from './TourGuidesSlideSkeleton.vue';
  import type { CityInfo, CountryInfo, RegionInfo } from '@/shared/api/experience';
  import { computed, ref } from 'vue';
  import { UiButton, UiButtonColorMode, UiButtonVariant } from '@experience/core-button-ui';
  import {
    type ExpTypes,
    type SortingTypes,
    useGuidesList,
    type GuideType,
  } from '@/widgets/guidesBlock/api/useGuidesList.ts';
  import { useScreenSize } from '@experience/shared';

  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import type { ContextGeoSubject } from '@/shared/types/geography';
  import { useIsRussianTourLocation } from '@/entities/city';
  import { trackEvent } from '@/entities/sogu';
  import { LocationType } from '@/entities/location';
  import { useLoaderStore } from '@/shared/store/useLoaderStore.ts';
  import { makeFullLink } from '@/shared/lib/makeFullLink';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';

  type MessageSchema = typeof ru;

  const { t } = useI18n<
    {
      message: MessageSchema;
    },
    'ru'
  >({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    slug: string;
    location: string;
    geoObject: CityInfo | CountryInfo | RegionInfo;
    defaultSoguFields: ContextGeoSubject;
  }

  const props = defineProps<Props>();

  const loaderStore = useLoaderStore();

  const { isSkeletonActive } = useListingSkeleton();
  const guidesBlock = ref<HTMLElement | null>(null);
  const { isRussianLocation } = useIsRussianTourLocation();

  const { isMobile } = useScreenSize();

  const urlParam = computed(() => {
    const baseParams = {
      page_size: 5,
      sorting: '-review_rating' as SortingTypes,
      guide_types: 'guide' as GuideType,
      tours_count_min: 1,
    };

    return {
      ...baseParams,
      [props.location]: props.slug,
      exp_types: 'tour' as ExpTypes,
    };
  });
  const { guidesList } = useGuidesList(urlParam.value);

  const currentList = computed(
    () => guidesList.value?.results?.filter((guide) => (guide.tours_count_published ?? 0) > 0) ?? [],
  );

  const headString = computed(() => {
    const toursPrefix = isRussianLocation.value
      ? t('guides_section.mday_tour_organizer')
      : t('guides_section.tour_organizer');

    return `${toursPrefix} ${slugStr.value}`;
  });

  const slugStr = computed(() => props.geoObject?.across_obj_phrase || '');

  const currentComponent = computed(() => (isMobile.value ? GuidesSlider : GuidesList));
  const currentComponentSkeleton = computed(() => (isMobile.value ? TourGuidesSlideSkeleton : TourGuidesListSkeleton));

  const allGuidesButtonClickHandler = () => {
    trackEvent('listing:guides:view-all-button.click', { ...props.defaultSoguFields.soguStamp });
  };

  const showAllButton = computed(
    () =>
      props.location === LocationType.COUNTRY &&
      currentList.value.length > 0 &&
      !!guidesList.value?.count &&
      currentList.value.length < guidesList.value.count,
  );

  const pathToGuides = computed(() => {
    const { slug } = props;

    return makeFullLink(`/destinations/${slug}/guides/`);
  });
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .guides-wrap {
    margin-top: 80px;

    .feature-title {
      @include typography.base-800-750-extra;

      @include adaptivity.tablet {
        @include typography.base-700-500-extra;
      }
    }

    .guides__list {
      margin-top: 24px;
    }

    .guides__btn {
      display: flex;
      justify-content: center;
      margin: 40px 0 0;

      .button {
        --btn-width: 297px;
      }

      @include adaptivity.mobile {
        box-sizing: border-box;
        margin-top: 16px;

        .button {
          --btn-width: 100%;
        }
      }
    }

    @include adaptivity.tablet {
      margin-top: 56px;
    }

    @include adaptivity.mobile {
      .feature-title {
        margin-bottom: 16px;
      }
    }
  }
</style>
